import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl } from '@angular/forms';

import { allRights } from '@common/all-rights';
import { CommonCommonModule } from '@common/common-common.module';

interface RightValue {
  title: string;
  key: string;
  depends: string[];
}

interface RightInfo {
  power: number;
  title: string;
  key: string;
  depends: string[];
}

interface Right {
  title: string;
  value: number;
  safe: boolean;
  depends: string[];
}

type Rights = Record<
  string,
  | {
      standard?: Record<string, Right>;
      special?: Record<string, Right>;
    }
  | Record<string, Right>
>;

@Component({
  selector: 'app-helper-rights',
  templateUrl: './helper-rights.component.html',
  styleUrls: ['./helper-rights.component.less'],
  imports: [CommonCommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class HelperRightsComponent {
  private readonly STORAGE_KEY = 'rights-helper-disabled';
  readonly rightGroups = ['common', 'objects', 'object_groups', 'users', 'report_templates'];
  readonly rightsGroupControl = new FormControl(this.rightGroups[0]);
  readonly numberControl = new FormControl(0);
  readonly doNotShowControl = new FormControl(false);

  open = true;
  powersInfo: RightInfo[] = [];

  constructor() {
    this.numberControl.valueChanges.subscribe(this.updatePowersInfo.bind(this));
    this.rightsGroupControl.valueChanges.subscribe(() => {
      const value = this.numberControl.value;
      if (value !== null) {
        this.updatePowersInfo(value);
      }
    });

    this.doNotShowControl.valueChanges.subscribe((value) => {
      if (value) {
        localStorage.setItem(this.STORAGE_KEY, 'true');
        this.open = false;
      }
    });
    const isDisabled = localStorage.getItem(this.STORAGE_KEY) === 'true';
    this.open = !isDisabled;
  }

  private rightsMap: Map<string, Map<number, RightValue>> = this.createRightsMap();

  private createRightsMap(): Map<string, Map<number, RightValue>> {
    const map = new Map<string, Map<number, RightValue>>();

    Object.entries(allRights as Rights).forEach(([group, rights]) => {
      const groupMap = new Map<number, RightValue>();

      if (group === 'common') {
        Object.entries(rights as Record<string, Right>).forEach(([key, right]) => {
          if (right.value) {
            groupMap.set(right.value, {
              title: right.title,
              key,
              depends: right.depends,
            });
          }
        });
      } else {
        const typedRights = rights as {
          standard?: Record<string, Right>;
          special?: Record<string, Right>;
        };

        if (typedRights.standard) {
          Object.entries(typedRights.standard).forEach(([key, right]) => {
            if (right.value) {
              groupMap.set(right.value, {
                title: right.title,
                key,
                depends: right.depends,
              });
            }
          });
        }

        if (typedRights.special) {
          Object.entries(typedRights.special).forEach(([key, right]) => {
            if (right.value) {
              groupMap.set(right.value, {
                title: right.title,
                key,
                depends: right.depends,
              });
            }
          });
        }
      }

      map.set(group, groupMap);
    });

    return map;
  }

  private updatePowersInfo(value: number | null): void {
    if (value !== null) {
      const powers = this.getPowersOfTwo(value);
      const group = this.rightsGroupControl.value || 'common';
      const groupMap = this.rightsMap.get(group);

      this.powersInfo = powers.map((power) => {
        const right = groupMap?.get(power);
        return {
          power,
          title: right?.title || 'Неизвестное право',
          key: right?.key || 'unknown',
          depends: right?.depends || [],
        };
      });
    }
  }

  private getPowersOfTwo(num: number): number[] {
    const result = [];
    let power = 1;

    while (num > 0) {
      if (num & 1) {
        result.push(power);
      }
      num >>= 1;
      power *= 2;
    }

    return result;
  }

  hasDependencies(info: RightInfo): boolean {
    return info.depends && info.depends.length > 0;
  }

  getDependsHint(depends: string[]): string {
    return `Зависит от:\n${depends.join('\n')}`;
  }

  toggle(value: boolean): void {
    if (!value && this.doNotShowControl.value) {
      localStorage.setItem(this.STORAGE_KEY, 'true');
    }
    this.open = value;
  }
}
